import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Bar, Line } from "react-chartjs-2";
import { Chart, elements, registerables } from "chart.js";
import { BeatLoader } from "react-spinners";
import "../../../../../styles/count_graphs.css";
import {
  barBackgroundColorLineCrossing,
  barBackgroundColorReverseLineCrossing,
} from "../../../../../config/index";
import {
  BiFullscreen,
  BiExitFullscreen,
  BiLineChart,
  BiBarChartAlt2,
  BiGitCompare,
} from "react-icons/bi";
import { MdSwapVert } from "react-icons/md";
import { transformDailyDataForComparison } from "../baseSuiteUtils";
import HourlyCountChart from "./HourlyCountGraph";

// Hourglass Icon Component
const HourglassIcon = ({ size = 24, color = "white" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 2V8C6 10.2091 7.79086 12 10 12C7.79086 12 6 13.7909 6 16V22H18V16C18 13.7909 16.2091 12 14 12C16.2091 12 18 10.2091 18 8V2H6Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M6 2H18" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M6 22H18" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Calendar Icon Component
const CalendarIcon = ({ size = 24, color = "white" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DailyCountChart = ({
  selectedYear,
  dailyCountData,
  rawDailyData,
  dailyDataLoading,
  selectedMonth,
  selectedWeekStartDate,
  selectedDayOfWeek,
  selectedDate,
  handleDailyBarClick,
  dailyVisitors,
  dailyVisitorPercentageIncrease,
  // Add these new props for hourly functionality
  hourlyCountData,
  rawHourlyData,
  hourlyDataLoading,
  handleHourlyBarClick,
  fetchHourlyCountData,
  isTrafficView = false,
  setIsTrafficView,
  isCompareMode = false,
  setIsCompareMode,
  processedCameraData = [],
  isHourlyTrafficView = false,
  setIsHourlyTrafficView,
  isHourlyCompareMode = false,
  setIsHourlyCompareMode,
}) => {
  // // console.log('DailyCountChart received props:', {
  //     selectedYear,
  //     selectedMonth,
  //     selectedDate,
  //     selectedDayOfWeek,
  //     hourlyCountData: hourlyCountData?.length,
  //     hourlyDataLoading,
  //     hasHourlyCountData: !!hourlyCountData,
  //     hasFetchFunction: !!fetchHourlyCountData
  // });
  const [chartData, setChartData] = useState();
  const [barChartData, setBarChartData] = useState();
  const [compareChartData, setCompareChartData] = useState(null);
  const [selectedWeekOfMonth, setSelectedWeekOfMonth] = useState();
  const [chartType, setChartType] = useState("line");
  const [hourlyChartType, setHourlyChartType] = useState("line"); // Separate state for hourly chart
  const [showHourlyView, setShowHourlyView] = useState(false);
  const [chartKey, setChartKey] = useState(0); // Key to force chart remount
  const [localSelectedDay, setLocalSelectedDay] = useState(null); // Store the clicked day locally

  // Auto-enable traffic view when compare mode is turned on
  useEffect(() => {
    if (isCompareMode && !isTrafficView) {
      setIsTrafficView(true);
    }
  }, [isCompareMode]);

  const getMonthName = (monthNumber) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months[monthNumber - 1];
  };

  const smallScreen = window.innerWidth < 786;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chartRef = useRef(null);

  // Check if there are multiple cameras available for comparison
  const hasMultipleCameras = useMemo(() => {
    if (!processedCameraData || processedCameraData.length === 0) return false;
    const cameras = processedCameraData.filter(
      (cam) => cam.camera_id !== "All" && cam.camera_id !== null
    );
    return cameras.length > 1;
  }, [processedCameraData]);

  // Calculate totals differently based on compare mode
  const { totalPeopleIn, totalPeopleOut } = useMemo(() => {
    if (isCompareMode && rawDailyData && processedCameraData) {
      // In compare mode: calculate totals from raw data for each camera
      const cameras = processedCameraData.filter(
        (cam) => cam.camera_id !== "All" && cam.camera_id !== null
      );

      let totalIn = 0;
      let totalOut = 0;

      cameras.forEach((camera) => {
        const cameraData = rawDailyData.filter(
          (d) => d.camera_id === camera.camera_id
        );

        const cameraInSum = cameraData
          .filter((d) => d.event_type === "Line Crossing")
          .reduce((sum, d) => sum + parseInt(d.total_count, 10), 0);

        const cameraOutSum = cameraData
          .filter((d) => d.event_type === "Reverse Line Crossing")
          .reduce((sum, d) => sum + parseInt(d.total_count, 10), 0);

        totalIn += cameraInSum;
        totalOut += cameraOutSum;
      });

      return { totalPeopleIn: totalIn, totalPeopleOut: totalOut };
    } else {
      // Normal mode: sum from aggregated daily data
      const totalIn = dailyCountData.reduce(
        (sum, d) => sum + d.event_types["Line Crossing"],
        0
      );
      const totalOut = dailyCountData.reduce(
        (sum, d) => sum + d.event_types["Reverse Line Crossing"],
        0
      );
      return { totalPeopleIn: totalIn, totalPeopleOut: totalOut };
    }
  }, [isCompareMode, rawDailyData, processedCameraData, dailyCountData]);

  // Prepare comparison data when in compare mode
  useEffect(() => {
    if (isCompareMode && rawDailyData && processedCameraData.length > 0) {
      const { cameraDatasets, allDays } = transformDailyDataForComparison(
        rawDailyData,
        processedCameraData,
        isTrafficView
      );

      setCompareChartData({
        labels: allDays,
        datasets: cameraDatasets,
      });
    }
  }, [isCompareMode, rawDailyData, processedCameraData, isTrafficView]);

  // Enhanced handleDailyBarClick to also fetch hourly data and switch to hourly view
  const handleDayClick = useCallback(
    (dayData) => {
      // console.log("Day clicked:", dayData);
      // console.log("About to fetch hourly data for:", {
        // selectedYear,
        // selectedMonth,
        // dayNumber: dayData.day_number,
        // hasFetchFunction: !!fetchHourlyCountData,
    //   });

      // Store the clicked day locally FIRST so it's available immediately
      setLocalSelectedDay(dayData);

      // Call the original handler
      if (handleDailyBarClick) {
        handleDailyBarClick(dayData);
      }

      // Automatically switch to hourly view when a day is clicked
      if (dayData && selectedYear && selectedMonth) {
        setShowHourlyView(true);
        // Fetch hourly data if the function is provided
        if (fetchHourlyCountData) {
          // console.log("Calling fetchHourlyCountData");
          fetchHourlyCountData(selectedYear, selectedMonth, dayData.day_number);
        } else {
          console.error("fetchHourlyCountData function not available");
        }
      }
    },
    [handleDailyBarClick, fetchHourlyCountData, selectedYear, selectedMonth]
  );

  const peakHour = useMemo(() => {
    if (!hourlyCountData || hourlyCountData.length === 0) {
      return null;
    }

    // Group data by hour (same logic as in HourlyCountChart)
    const hourlyGroups = {};

    hourlyCountData.forEach((record) => {
      const hour = record.hour;
      if (!hourlyGroups[hour]) {
        hourlyGroups[hour] = {
          hour: hour,
          hour_display: record.hour_display,
          event_types: {
            "Line Crossing": 0,
            "Reverse Line Crossing": 0,
          },
        };
      }
      hourlyGroups[hour].event_types[record.event_type] +=
        parseInt(record.total_count) || 0;
    });

    // Convert to array and find peak
    const processedHourlyData = Object.values(hourlyGroups).sort(
      (a, b) => a.hour - b.hour
    );

    if (processedHourlyData.length === 0) {
      return null;
    }

    return processedHourlyData.reduce((max, current) => {
      const currentTotal =
        current.event_types["Line Crossing"] +
        current.event_types["Reverse Line Crossing"];
      const maxTotal =
        max.event_types["Line Crossing"] +
        max.event_types["Reverse Line Crossing"];
      return currentTotal > maxTotal ? current : max;
    }, processedHourlyData[0]);
  }, [hourlyCountData]);

  // Toggle between daily and hourly views
  const toggleView = () => {
    // console.log(
    //   "Toggling view from",
    //   showHourlyView ? "hourly" : "daily",
    //   "to",
    //   !showHourlyView ? "hourly" : "daily"
    // );
    // // console.log("Current data state:", {
    //   selectedDate,
    //   selectedDayOfWeek,
    //   selectedMonth,
    //   selectedYear,
    //   hourlyCountData: hourlyCountData?.length,
    //   hourlyDataLoading,
    //   hasHourlyData: hourlyCountData && hourlyCountData.length > 0,
    //   hasFetchFunction: !!fetchHourlyCountData,
    // });

    setShowHourlyView(!showHourlyView);

    // If switching to hourly view and we don't have data, try to fetch it
    if (
      !showHourlyView &&
      selectedDate &&
      selectedMonth &&
      selectedYear &&
      fetchHourlyCountData
    ) {
      // console.log(" Fetching hourly data for toggle:", {
    //     year: selectedYear,
    //     month: selectedMonth,
    //     day: selectedDate,
    //   });
      fetchHourlyCountData(selectedYear, selectedMonth, selectedDate);
    } else if (!showHourlyView) {
      // console.log(" Cannot fetch hourly data:", {
    //     selectedDate: !!selectedDate,
    //     selectedMonth: !!selectedMonth,
    //     selectedYear: !!selectedYear,
    //     fetchHourlyCountData: !!fetchHourlyCountData,
    //   });
    }
  };

  const prepareDailyCountLineChart = () => {
    if (isTrafficView) {
      return {
        labels: dailyCountData.map((d) => d.day),
        datasets: [
          {
            label: "Traffic (Average)",
            data: dailyCountData.map(
              (d) =>
                (d.event_types["Line Crossing"] +
                  d.event_types["Reverse Line Crossing"]) /
                2
            ),
            backgroundColor: "#FFA500",
            borderColor: "#FFA500",
            borderWidth: 3,
            tension: 0.3,
          },
        ],
      };
    } else {
      return {
        labels: dailyCountData.map((d) => d.day),
        datasets: [
          {
            label: "People In",
            data: dailyCountData.map((d) => d.event_types["Line Crossing"]),
            backgroundColor: barBackgroundColorLineCrossing,
            borderColor: barBackgroundColorLineCrossing,
            borderWidth: 3,
            tension: 0.3,
          },
          {
            label: "People Out",
            data: dailyCountData.map(
              (d) => d.event_types["Reverse Line Crossing"]
            ),
            backgroundColor: barBackgroundColorReverseLineCrossing,
            borderColor: barBackgroundColorReverseLineCrossing,
            borderWidth: 3,
            tension: 0.3,
          },
        ],
      };
    }
  };

  const getDailyLineChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const dayData = dailyCountData[index];
          handleDayClick(dayData);
        }
      },
      plugins: {
        legend: {
          labels: {
            color: "#F8F8F8",
          },
          onClick: (event, legendItem, legend) => {
            // Enable clicking legend items to toggle camera visibility
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            const meta = chart.getDatasetMeta(index);

            // Toggle visibility
            meta.hidden =
              meta.hidden === null ? !chart.data.datasets[index].hidden : null;
            chart.update();
          },
          onHover: (event) => {
            event.native.target.style.cursor = "pointer";
          },
          onLeave: (event) => {
            event.native.target.style.cursor = "default";
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: (context) => {
              const labelDay = dailyCountData[context.index]?.day;
              return labelDay === selectedDayOfWeek ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const labelDay = dailyCountData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? "bold" : "normal";
              },
              size: (context) => {
                const labelDay = dailyCountData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? 14 : 12;
              },
            },
          },
        },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 12 }, color: "#F8F8F8" },
        },
      },
    };
  };

  const prepareDailyCountBarChart = () => {
    if (isTrafficView) {
      return {
        labels: dailyCountData.map((d) => d.day),
        datasets: [
          {
            label: "Traffic (Average)",
            data: dailyCountData.map(
              (d) =>
                (d.event_types["Line Crossing"] +
                  d.event_types["Reverse Line Crossing"]) /
                2
            ),
            backgroundColor: "#FFA500",
            borderColor: dailyCountData.map((d) =>
              selectedDate === d.day_number ? "#ccc" : "#FF8C00"
            ),
            borderWidth: 1,
          },
        ],
      };
    } else {
      return {
        labels: dailyCountData.map((d) => d.day),
        datasets: [
          {
            label: "People In",
            data: dailyCountData.map((d) => d.event_types["Line Crossing"]),
            backgroundColor: barBackgroundColorLineCrossing,
            borderColor: dailyCountData.map((d) =>
              selectedDate === d.day_number ? "#ccc" : "#8E338C"
            ),
            borderWidth: 1,
          },
          {
            label: "People Out",
            data: dailyCountData.map(
              (d) => d.event_types["Reverse Line Crossing"]
            ),
            backgroundColor: barBackgroundColorReverseLineCrossing,
            borderColor: dailyCountData.map((d) =>
              selectedDate === d.day_number ? "#ccc" : "#8E338C"
            ),
            borderWidth: 1,
          },
        ],
      };
    }
  };

  const getBarChartOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: !isFullscreen,
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const dayData = dailyCountData[index];
          handleDayClick(dayData);
        }
      },
      plugins: {
        legend: {
          labels: {
            color: "white",
          },
          onClick: (event, legendItem, legend) => {
            // Enable clicking legend items to toggle camera visibility
            const index = legendItem.datasetIndex;
            const chart = legend.chart;
            const meta = chart.getDatasetMeta(index);

            // Toggle visibility
            meta.hidden =
              meta.hidden === null ? !chart.data.datasets[index].hidden : null;
            chart.update();
          },
          onHover: (event) => {
            event.native.target.style.cursor = "pointer";
          },
          onLeave: (event) => {
            event.native.target.style.cursor = "default";
          },
        },
      },
      scales: {
        x: {
          stacked: !isTrafficView && !isCompareMode,
          grid: { display: false },
          ticks: {
            color: (context) => {
              const labelDay = dailyCountData[context.index]?.day;
              return labelDay === selectedDayOfWeek ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const labelDay = dailyCountData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? "bold" : "normal";
              },
              size: (context) => {
                const labelDay = dailyCountData[context.index]?.day;
                return labelDay === selectedDayOfWeek ? 14 : 12;
              },
            },
          },
          barPercentage: 0.5,
          categoryPercentage: 0.5,
        },
        y: {
          stacked: !isTrafficView && !isCompareMode,
          beginAtZero: true,
          ticks: { font: { size: 12 }, color: "#F8F8F8" },
        },
      },
    };
  };

  const toggleChartType = () => {
    if (showHourlyView) {
      // console.log(
    //     "toggleChartType for HOURLY, changing from:",
    //     hourlyChartType,
    //     "to:",
    //     hourlyChartType === "bar" ? "line" : "bar"
    //   );
      setHourlyChartType((prevType) => (prevType === "bar" ? "line" : "bar"));
    } else {
      // console.log(
    //     "toggleChartType for DAILY, changing from:",
    //     chartType,
    //     "to:",
    //     chartType === "bar" ? "line" : "bar"
    //   );
      setChartType((prevType) => (prevType === "bar" ? "line" : "bar"));
    }
  };

  // Force chart resize when entering/exiting fullscreen
  useEffect(() => {
    const timers = [];

    // Force chart remount when exiting fullscreen to reset sizing
    if (!isFullscreen) {
      setChartKey((prev) => prev + 1);
    }

    // Force immediate DOM reflow
    if (chartRef.current?.canvas) {
      const container = chartRef.current.canvas.parentElement;
      if (container) {
        // Force browser to recalculate layout
        void container.offsetHeight;
      }
    }

    // First resize immediately
    if (chartRef.current) {
      chartRef.current.resize();
    }

    // Second resize after a short delay
    timers.push(
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.resize();
          chartRef.current.update("none");
        }
      }, 50)
    );

    // Third resize after CSS transition
    timers.push(
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.resize();
          chartRef.current.update("none");
        }
      }, 350)
    );

    // Final resize to ensure everything is settled
    timers.push(
      setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.resize();
          chartRef.current.update("none");
        }
      }, 500)
    );

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [isFullscreen]);

  // Handle window resize events in fullscreen mode
  useEffect(() => {
    if (!isFullscreen) return;

    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.resize();
        chartRef.current.update("none");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFullscreen]);

  useEffect(() => {
    if (dailyCountData.length) {
      const preparedLineChart = prepareDailyCountLineChart();
      const preparedBarChart = prepareDailyCountBarChart();
      if (preparedLineChart) {
        setChartData(preparedLineChart);
      }

      if (preparedBarChart) {
        setBarChartData(preparedBarChart);
      }
    }
  }, [
    dailyCountData,
    selectedDate,
    chartType,
    isTrafficView,
    isFullscreen,
    handleDayClick,
  ]);

  Chart.register(...registerables);

  return (
    <div
      className={`${
        dailyDataLoading ? "daily-bar-chart-container-loading" : ""
      } ${
        isFullscreen
          ? "count-bar-chart-wrapper fullscreen-container"
          : "count-bar-chart-wrapper"
      }`}
    >
      {dailyCountData.length > 0 ? (
        <>
          {showHourlyView ? (
            // Hourly View
            <div style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="graph-title"
                  style={{
                    flex: "0 1 auto",
                    minWidth: "0",
                    maxWidth: "fit-content",
                  }}
                >
                  {localSelectedDay?.day || selectedDayOfWeek} Hourly
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flexWrap: "nowrap",
                    flexShrink: 1,
                  }}
                >
                  <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
                    {/* Compare Mode Toggle Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={() =>
                        setIsHourlyCompareMode(!isHourlyCompareMode)
                      }
                      title={
                        !hasMultipleCameras
                          ? "Multiple cameras required for comparison"
                          : isHourlyCompareMode
                          ? "Switch to Single Camera View"
                          : "Compare All Cameras"
                      }
                      disabled={!hasMultipleCameras}
                      style={{
                        backgroundColor: isHourlyCompareMode
                          ? "#32de84"
                          : undefined,
                        opacity: !hasMultipleCameras ? 0.5 : 1,
                        cursor: !hasMultipleCameras ? "not-allowed" : "pointer",
                      }}
                    >
                      <BiGitCompare size={24} color="white" />
                    </button>

                    {/* Traffic View Toggle Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={() =>
                        setIsHourlyTrafficView(!isHourlyTrafficView)
                      }
                      title={
                        isHourlyCompareMode
                          ? "Traffic view is required in compare mode"
                          : isHourlyTrafficView
                          ? "Switch to In/Out Count View"
                          : "Switch to Traffic (Average) View"
                      }
                      disabled={isHourlyCompareMode}
                      style={{
                        backgroundColor: isHourlyTrafficView
                          ? "#FFA500"
                          : undefined,
                        opacity: isHourlyCompareMode ? 0.5 : 1,
                        cursor: isHourlyCompareMode ? "not-allowed" : "pointer",
                      }}
                    >
                      <MdSwapVert size={24} color="white" />
                    </button>

                    {/* Back to Daily View Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={toggleView}
                      title="Back to Daily View"
                    >
                      <CalendarIcon size={24} color="white" />
                    </button>

                    {/* Line/Bar Chart Toggle Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={() => {
                        // console.log(
                        //   "Header toggle clicked in hourly view, current type:",
                        //   hourlyChartType
                        // );
                        toggleChartType();
                      }}
                    >
                      {hourlyChartType === "bar" ? (
                        <BiLineChart
                          size={24}
                          title="Switch to Line Chart"
                          color="white"
                        />
                      ) : (
                        <BiBarChartAlt2
                          size={24}
                          title="Switch to Bar Chart"
                          color="white"
                        />
                      )}
                    </button>

                    <button
                      className="toggle-chart-button"
                      onClick={() => setIsFullscreen((prev) => !prev)}
                      title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    >
                      {isFullscreen ? (
                        <BiExitFullscreen size={24} color="white" />
                      ) : (
                        <BiFullscreen size={24} color="white" />
                      )}
                    </button>
                  </div>

                  {peakHour && (
                    <div className="count-chart-footer-percentage-container">
                      <div
                        className="count-chart-footer-percentage-value"
                        style={{ color: "#32de84" }}
                      >
                        Peak: {peakHour.hour_display}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Render hourly chart or debug info */}
              {hourlyDataLoading ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <BeatLoader
                    color={"white"}
                    loading={hourlyDataLoading}
                    size={15}
                  />
                  <div style={{ color: "white", marginTop: "1rem" }}>
                    Loading hourly data...
                  </div>
                </div>
              ) : hourlyCountData && hourlyCountData.length > 0 ? (
                <HourlyCountChart
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                  selectedDate={localSelectedDay?.day_number || selectedDate}
                  selectedDayOfWeek={localSelectedDay?.day || selectedDayOfWeek}
                  hourlyCountData={hourlyCountData}
                  rawHourlyData={rawHourlyData}
                  hourlyDataLoading={hourlyDataLoading}
                  handleHourlyBarClick={handleHourlyBarClick}
                  chartType={hourlyChartType}
                  onToggleChartType={toggleChartType}
                  isTrafficView={isHourlyTrafficView}
                  setIsTrafficView={setIsHourlyTrafficView}
                  isCompareMode={isHourlyCompareMode}
                  setIsCompareMode={setIsHourlyCompareMode}
                  processedCameraData={processedCameraData}
                  isFullscreen={isFullscreen}
                />
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#666",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    margin: "1rem 0",
                  }}
                >
                  <div>
                    No hourly data available for{" "}
                    {localSelectedDay?.day || selectedDayOfWeek}{" "}
                    {localSelectedDay?.day_number || selectedDate}
                  </div>
                  <div style={{ fontSize: "0.9rem", marginTop: "0.5rem" }}>
                    Data length: {hourlyCountData?.length || 0}
                  </div>
                  <button
                    onClick={() => {
                      if (fetchHourlyCountData) {
                        // console.log("🧪 Manual test fetch:", {
                        //   year: selectedYear,
                        //   month: selectedMonth,
                        //   day: localSelectedDay?.day_number || selectedDate,
                        // });
                        fetchHourlyCountData(
                          selectedYear,
                          selectedMonth,
                          localSelectedDay?.day_number || selectedDate
                        );
                      } else {
                        // console.log("❌ No fetchHourlyCountData function");
                      }
                    }}
                    style={{
                      marginTop: "1rem",
                      padding: "0.5rem 1rem",
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Retry Loading Hourly Data
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Daily View
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="graph-title"
                  style={{
                    flex: "0 1 auto",
                    minWidth: "0",
                    maxWidth: "fit-content",
                  }}
                >
                  {selectedDayOfWeek || "Daily"}{" "}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flexWrap: "nowrap",
                    flexShrink: 1,
                  }}
                >
                  <div style={{ display: "flex", gap: "10px", flexShrink: 0 }}>
                    {/* Compare Mode Toggle Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={() => setIsCompareMode(!isCompareMode)}
                      title={
                        !hasMultipleCameras
                          ? "Multiple cameras required for comparison"
                          : isCompareMode
                          ? "Switch to Single Camera View"
                          : "Compare All Cameras"
                      }
                      disabled={!hasMultipleCameras}
                      style={{
                        backgroundColor: isCompareMode ? "#32de84" : undefined,
                        opacity: !hasMultipleCameras ? 0.5 : 1,
                        cursor: !hasMultipleCameras ? "not-allowed" : "pointer",
                      }}
                    >
                      <BiGitCompare size={24} color="white" />
                    </button>

                    {/* Traffic View Toggle Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={() => setIsTrafficView(!isTrafficView)}
                      title={
                        isCompareMode
                          ? "Traffic view is required in compare mode"
                          : isTrafficView
                          ? "Switch to In/Out Count View"
                          : "Switch to Traffic (Average) View"
                      }
                      disabled={isCompareMode}
                      style={{
                        backgroundColor: isTrafficView ? "#FFA500" : undefined,
                        opacity: isCompareMode ? 0.5 : 1,
                        cursor: isCompareMode ? "not-allowed" : "pointer",
                      }}
                    >
                      <MdSwapVert size={24} color="white" />
                    </button>

                    {/* Hourly View Toggle Button */}
                    <button
                      className="toggle-chart-button"
                      onClick={toggleView}
                      title={
                        selectedDate
                          ? "View Hourly Data"
                          : "Select a day to view hourly data"
                      }
                      disabled={!selectedDate}
                      style={{
                        opacity: selectedDate ? 1 : 0.5,
                        cursor: selectedDate ? "pointer" : "not-allowed",
                      }}
                    >
                      <HourglassIcon size={24} color="white" />
                    </button>

                    <button
                      className="toggle-chart-button"
                      onClick={toggleChartType}
                    >
                      {chartType === "bar" ? (
                        <BiLineChart
                          size={24}
                          title="Switch to Line Chart"
                          color="white"
                        />
                      ) : (
                        <BiBarChartAlt2
                          size={24}
                          title="Switch to Bar Chart"
                          color="white"
                        />
                      )}
                    </button>
                    <button
                      className="toggle-chart-button"
                      onClick={() => setIsFullscreen((prev) => !prev)}
                      title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    >
                      {isFullscreen ? (
                        <BiExitFullscreen size={24} color="white" />
                      ) : (
                        <BiFullscreen size={24} color="white" />
                      )}
                    </button>
                  </div>

                  <div className="count-chart-footer-percentage-container">
                    <div className="count-chart-footer-percentage-icon">
                      {dailyVisitorPercentageIncrease > 0 ? (
                        <svg
                          style={{ color: "#32de84" }}
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi bi-arrow-up-right-circle"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z"
                          />
                        </svg>
                      ) : (
                        <svg
                          style={{ color: "#EF5B5B" }}
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi bi-arrow-down-right-circle"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z"
                          />
                        </svg>
                      )}
                    </div>
                    <div
                      className="count-chart-footer-percentage-value"
                      style={{
                        color:
                          dailyVisitorPercentageIncrease > 0
                            ? "#32de84"
                            : "#EF5B5B",
                      }}
                    >
                      {dailyVisitorPercentageIncrease}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="chart-total-container">
                <div className="chart-total-pill">
                  {isTrafficView ? (
                    <div
                      className="chart-total-label"
                      style={{ color: "#FFA500" }}
                    >
                      Total Traffic (Average):{" "}
                      {Math.round((totalPeopleIn + totalPeopleOut) / 2)}
                    </div>
                  ) : (
                    <>
                      <div
                        className="chart-total-label"
                        style={{ color: "#64C6E6" }}
                      >
                        Total In: {totalPeopleIn}
                      </div>
                      <div
                        className="chart-total-label"
                        style={{ color: "#8E338C" }}
                      >
                        Total Out: {totalPeopleOut}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                {chartType === "line" ? (
                  <Line
                    key={`daily-line-${chartKey}`}
                    ref={chartRef}
                    data={
                      isCompareMode && compareChartData
                        ? compareChartData
                        : chartData || { labels: [], datasets: [] }
                    }
                    options={getDailyLineChartOptions()}
                  />
                ) : (
                  <Bar
                    key={`daily-bar-${chartKey}`}
                    ref={chartRef}
                    data={
                      isCompareMode && compareChartData
                        ? compareChartData
                        : barChartData || { labels: [], datasets: [] }
                    }
                    options={getBarChartOptions()}
                  />
                )}
                {dailyDataLoading && (
                  <div id="monthly-loading-overlay">
                    <BeatLoader
                      color={"white"}
                      loading={dailyDataLoading}
                      size={15}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <div style={{ display: "none" }} />
      )}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(DailyCountChart, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render), false if different (re-render)
  return (
    prevProps.selectedYear === nextProps.selectedYear &&
    prevProps.selectedMonth === nextProps.selectedMonth &&
    prevProps.selectedDate === nextProps.selectedDate &&
    prevProps.selectedDayOfWeek === nextProps.selectedDayOfWeek &&
    prevProps.dailyDataLoading === nextProps.dailyDataLoading &&
    prevProps.hourlyDataLoading === nextProps.hourlyDataLoading &&
    prevProps.isTrafficView === nextProps.isTrafficView &&
    prevProps.isCompareMode === nextProps.isCompareMode &&
    prevProps.isHourlyTrafficView === nextProps.isHourlyTrafficView &&
    prevProps.isHourlyCompareMode === nextProps.isHourlyCompareMode &&
    // Compare data by reference (will change when new data fetched)
    prevProps.dailyCountData === nextProps.dailyCountData &&
    prevProps.rawDailyData === nextProps.rawDailyData &&
    prevProps.hourlyCountData === nextProps.hourlyCountData &&
    prevProps.rawHourlyData === nextProps.rawHourlyData &&
    prevProps.processedCameraData === nextProps.processedCameraData
  );
});
